import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Progress, Spin, Upload, UploadProps, message } from 'antd';
import {
  InboxOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import PublicActionPage from '../components/PublicActionPage';
import KofiSupportPanel from '../support/KofiSupportPanel';
import ToolRunAllowance from '../support/ToolRunAllowance';
import { KofiRequiredError } from '../support/kofi.service';
import { useToolGate } from '../support/useToolGate';
import {
  AuthenticityReport,
  JobService,
  ResumeImproveJobStatus,
  ResumeImproveProgress,
} from './services/job.service';

const progressPercentByStatus: Record<ResumeImproveJobStatus, number> = {
  QUEUED: 15,
  PROCESSING: 55,
  COMPLETED: 100,
  FAILED: 0,
};

const ResumeAuthenticity: React.FC = () => {
  const [compare, setCompare] = useState(false);
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [fileSpinner, setFileSpinner] = useState(false);
  const [progressMessage, setProgressMessage] = useState(
    'Checking your resume...'
  );
  const [progressPercent, setProgressPercent] = useState(0);
  const [processError, setProcessError] = useState<string | null>(null);
  const [report, setReport] = useState<AuthenticityReport | null>(null);
  const { status, kofiRequired, setKofiRequired, refresh, onRedeemed } =
    useToolGate();

  const resetResults = () => {
    setReport(null);
    setProcessError(null);
    setFileSpinner(false);
    setOriginalFile(null);
    setProgressPercent(0);
  };

  const processFile = async (resume: File) => {
    setPendingFile(resume);
    setProcessError(null);
    setKofiRequired(null);
    setReport(null);
    try {
      setFileSpinner(true);
      setProgressPercent(10);
      setProgressMessage('Uploading your resume...');
      const result = await JobService.CheckAuthenticity(
        resume,
        compare ? originalFile : null,
        (progress: ResumeImproveProgress) => {
          setProgressMessage(progress.message);
          setProgressPercent(progressPercentByStatus[progress.status] ?? 10);
        }
      );
      setReport(result);
      message.success('Your authenticity report is ready.');
      void refresh();
    } catch (error: unknown) {
      if (error instanceof KofiRequiredError) {
        setKofiRequired(error);
        return;
      }
      const serverMessage = error instanceof Error ? error.message.trim() : '';
      setProcessError(
        serverMessage && !/^\d{3}\s*-/.test(serverMessage)
          ? serverMessage
          : "We couldn't check that resume. Try a text-based PDF or DOCX."
      );
    } finally {
      setFileSpinner(false);
    }
  };

  return (
    <PublicActionPage
      eyebrow="Resume authenticity"
      title="See which claims need your evidence"
      description="We flag dates, metrics, and skills the resume does not support. Compare two files to see what a revision added."
      icon={<SafetyCertificateOutlined />}
      steps={[
        { title: 'Upload the resume to review', detail: 'PDF or DOCX.' },
        {
          title: 'Optionally add the original',
          detail: 'We flag facts the revision added.',
        },
        {
          title: 'Answer the questions',
          detail: 'Flags are questions, not accusations.',
        },
      ]}
      asideNote="Your files stay only long enough to build the report."
      formEyebrow="Authenticity check"
      formTitle="Drop the resume to review"
      formDescription="The check starts as soon as the file is accepted."
      footer={
        <p className="jo-body-muted mb-0 text-center text-sm">
          Checking layout instead?{' '}
          <Link to="/ats-check" className="public-inline-link">
            ATS compatibility
          </Link>
          .
        </p>
      }
    >
      <div className="mx-auto w-full max-w-xl">
        <ToolRunAllowance status={status} />
        {!report && (
          <>
            <label className="mb-3 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={compare}
                onChange={(event) => setCompare(event.target.checked)}
              />
              Compare with an original resume
            </label>
            {compare && (
              <OriginalUpload
                file={originalFile}
                onFile={setOriginalFile}
                disabled={fileSpinner}
              />
            )}
            <ResumeDropzone
              disabled={fileSpinner || (compare && !originalFile)}
              spinning={fileSpinner}
              message={progressMessage}
              percent={progressPercent}
              onFile={processFile}
            />
            {compare && !originalFile && (
              <p className="jo-body-muted mt-2 text-sm">
                Add the original file before the revised resume.
              </p>
            )}
          </>
        )}

        {kofiRequired && !fileSpinner && (
          <KofiSupportPanel
            required={kofiRequired}
            onRedeemed={(next) => {
              onRedeemed(next);
              if (pendingFile) {
                void processFile(pendingFile);
              }
            }}
          />
        )}

        {processError && !fileSpinner && (
          <div
            className="public-result-panel public-result-panel--error mt-4"
            role="alert"
          >
            <p className="mb-3 text-sm font-semibold">{processError}</p>
            <Button
              className="public-button-secondary"
              icon={<ReloadOutlined />}
              onClick={resetResults}
            >
              Try another file
            </Button>
          </div>
        )}

        {report && (
          <div
            className="public-result-panel mt-2 text-left"
            role="status"
            aria-live="polite"
          >
            <h3 className="jo-heading mb-2 text-base font-black">
              {report.mode === 'COMPARE'
                ? 'Compared with the original'
                : 'Claims to double-check'}
            </h3>
            {report.flags.length === 0 ? (
              <p className="jo-body-subtle text-sm">
                No unsupported claims were found in the text we could read.
              </p>
            ) : (
              <ul className="m-0 list-none space-y-3 p-0">
                {report.flags.map((flag, index) => (
                  <li
                    key={`${flag.type}-${index}`}
                    className="public-improvement-item"
                  >
                    {flag.excerpt && (
                      <p className="mb-1 text-sm font-semibold">
                        {flag.excerpt}
                      </p>
                    )}
                    <p className="jo-body-subtle mb-0 text-sm">{flag.why}</p>
                    {flag.questionForCandidate && (
                      <p className="jo-body-muted mb-0 mt-1 text-sm">
                        {flag.questionForCandidate}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <p className="jo-body-muted mt-4 text-sm">{report.disclaimer}</p>
            <Button
              className="public-button-secondary mt-4"
              icon={<ReloadOutlined />}
              onClick={resetResults}
            >
              Check another
            </Button>
          </div>
        )}
      </div>
    </PublicActionPage>
  );
};

function OriginalUpload({
  file,
  onFile,
  disabled,
}: {
  file: File | null;
  onFile: (file: File | null) => void;
  disabled: boolean;
}) {
  const props: UploadProps = {
    multiple: false,
    maxCount: 1,
    accept: '.pdf,.docx',
    showUploadList: false,
    disabled,
    beforeUpload: rejectNonResume,
    async onChange(info) {
      if (info.file && info.file.status !== 'removed') {
        onFile((info.file.originFileObj || info.file) as File);
      }
    },
  };
  return (
    <div className="mb-4">
      <p className="public-form-label mb-2">Original resume</p>
      <Upload {...props}>
        <Button disabled={disabled}>
          {file ? file.name : 'Choose original PDF or DOCX'}
        </Button>
      </Upload>
    </div>
  );
}

function ResumeDropzone({
  disabled,
  spinning,
  message: progressMessage,
  percent,
  onFile,
}: {
  disabled: boolean;
  spinning: boolean;
  message: string;
  percent: number;
  onFile: (file: File) => Promise<void>;
}) {
  const { Dragger } = Upload;
  const props: UploadProps = {
    multiple: false,
    maxCount: 1,
    accept: '.pdf,.docx',
    showUploadList: false,
    disabled,
    beforeUpload: rejectNonResume,
    async onChange(info) {
      if (info.file && info.file.status !== 'removed') {
        await onFile((info.file.originFileObj || info.file) as File);
      }
    },
  };
  return (
    <Dragger
      {...props}
      className="public-upload-zone flex min-h-[180px] flex-col justify-center rounded-lg"
      aria-label="Resume file"
    >
      {spinning ? (
        <div
          className="flex w-full flex-col items-center gap-4 px-4"
          role="status"
          aria-live="polite"
        >
          <Spin />
          <Progress
            percent={percent}
            status="active"
            showInfo={false}
            className="w-full max-w-sm"
          />
          <p className="jo-body-subtle mb-0 text-center text-sm">
            {progressMessage}
          </p>
        </div>
      ) : (
        <>
          <p className="ant-upload-drag-icon">
            <InboxOutlined style={{ fontSize: '36px' }} />
          </p>
          <p className="ant-upload-text">Drag & drop or browse</p>
          <p className="ant-upload-hint">PDF or DOCX</p>
        </>
      )}
    </Dragger>
  );
}

function rejectNonResume(file: File) {
  const isPDF = file.type === 'application/pdf';
  const isDOCX =
    file.type ===
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  if (!isPDF && !isDOCX) {
    message.error('Only PDF or DOCX files are allowed.');
    return Upload.LIST_IGNORE;
  }
  return false;
}

export default ResumeAuthenticity;
